import { Action, action, thunk, Thunk } from 'easy-peasy'
import { positionToFEN } from '../../chess/fen'
import { createRootGameTree, getStartingPosition, undoLastMove } from '../../chess/gameTree'
import { BasePositionFEN } from '../../chess/positionPresets'
import { Color, GameTree } from '../../chess/types'
import { Arrow, Square } from '../../react-chessboard/src/chessboard/types'
import { analyticsManager } from '../../sharedComponents/src/globalHeader/services/analytics/AnalyticsManager'
import { FtueOptions, ITag, usedHint } from './functions/puzzlesApi'

export type PuzzlesModel = {
    puzzleId?: string
    flipped: boolean
    puzzleGameTree: GameTree
    progressCurrentPositionId: string
    gameTree: GameTree
    currentPositionId: string
    myColor: Color
    colorToMove: number
    allTags: Array<ITag>
    userRating: number
    hint?: Square
    hintsCounter: number
    availableHints: number
    customArrows: Array<Arrow>
    ftueOptions?: FtueOptions
    setGameTree: Action<PuzzlesModel, GameTree>
    setProgressCurrentPositionId: Action<PuzzlesModel, string>
    setCurrentPositionId: Action<PuzzlesModel, string>
    setFlipBoard: Action<PuzzlesModel, boolean>
    initialiseGame: Action<PuzzlesModel, { puzzleId: string, gameTree?: GameTree | undefined; isFirstMove?: boolean | undefined }>
    setColorToMove: Action<PuzzlesModel, number>
    undoLastMove: Action<PuzzlesModel, GameTree>
    setAllTags: Action<PuzzlesModel, Array<ITag>>
    resetGame: Action<PuzzlesModel>
    setUserRating: Action<PuzzlesModel, number>
    setHint: Action<PuzzlesModel, { from: Square, to?: Square } | undefined>
    setHintAsync: Thunk<PuzzlesModel, { from: Square, to?: Square } | undefined>
    clearHint: Action<PuzzlesModel>
    setAvailableHints: Action<PuzzlesModel, number>
    setFtueOptions: Action<PuzzlesModel, FtueOptions | undefined>
}

const initialGameTree = createRootGameTree(BasePositionFEN)
const initialCurrentPosition = getStartingPosition(initialGameTree).id

const PuzzlesViewModel: PuzzlesModel = {
    myColor: Color.White,
    flipped: false,
    puzzleGameTree: initialGameTree,
    progressCurrentPositionId: initialCurrentPosition,
    gameTree: initialGameTree,
    currentPositionId: initialCurrentPosition,
    colorToMove: 1,
    allTags: [],
    userRating: 0,
    hintsCounter: 0,
    availableHints: -1,
    customArrows: [],

    // ----------------------------------------------------------------------------- actions
    setGameTree: action((state, payload) => {
        state.gameTree = payload
        state.currentPositionId = getStartingPosition(payload).id
    }),

    setProgressCurrentPositionId: action((state, payload) => {
        state.progressCurrentPositionId = payload
    }),

    setCurrentPositionId: action((state, payload) => {
        state.currentPositionId = payload
    }),

    setFlipBoard: action((state, flipped) => {
        state.flipped = flipped
    }),

    initialiseGame: action((state, gd) => {
        const newPuzzleGameTree = gd.gameTree || initialGameTree
        state.puzzleGameTree = newPuzzleGameTree
        const startingProgressPosition = getStartingPosition(newPuzzleGameTree)
        state.progressCurrentPositionId = startingProgressPosition.id

        const newGameTree = createRootGameTree(positionToFEN(startingProgressPosition.position))
        state.gameTree = newGameTree
        state.currentPositionId = getStartingPosition(newGameTree).id

        if (gd.isFirstMove) {
            state.myColor = startingProgressPosition.position.turn
        } else if (startingProgressPosition.position.turn === Color.Black) {
            state.myColor = Color.White
        } else {
            state.myColor = Color.Black
        }

        state.flipped = state.myColor === Color.Black
        state.puzzleId = gd.puzzleId
    }),

    setColorToMove: action((state, number) => {
        state.colorToMove = number
    }),

    undoLastMove: action((state, payload) => {
        const previousPositionId = undoLastMove(payload).id || initialCurrentPosition
        state.currentPositionId = previousPositionId
        state.gameTree = payload
    }),

    setAllTags: action((state, tags) => {
        state.allTags = tags
    }),

    resetGame: action((state) => {
        state.myColor = Color.White
        state.puzzleGameTree = initialGameTree
        state.progressCurrentPositionId = initialCurrentPosition
        state.gameTree = initialGameTree
        state.currentPositionId = initialCurrentPosition
        state.colorToMove = 1
        state.puzzleId = undefined
    }),

    setUserRating: action((state, rating) => {
        state.userRating = rating
    }),

    setHint: action((state, payload) => {
        if (payload) {
            state.hint = payload.from
            state.hintsCounter += 1
            if (payload.to) {
                state.customArrows.push([payload.from, payload.to, '#C9F1AD', 0.8])
            }
        } else {
            state.hint = undefined
            state.customArrows = []
        }
    }),

    setHintAsync: thunk(async (actions, payload, helpers) => {
        try {
            const { getState } = helpers
            const state = getState()
            if (state.availableHints !== 0) {
                const result = await usedHint()
                actions.setHint(payload)
                actions.setAvailableHints(result.availableHints)
                analyticsManager.dispatchEvent('powerUpUsed', {
                    type: 'hints',
                    oldArgument: state.availableHints,
                    newArgument: result.availableHints,
                    argumentChange: state.availableHints - result.availableHints,
                    feature: 'practicePuzzle',
                    puzzleId: state.puzzleId || '',
                })
            }
        } catch (error) {
            console.error('[PUZZLES] Error in setHintAsync:', error)
        }
    }),

    clearHint: action((state) => {
        state.hint = undefined
        state.hintsCounter = 0
        state.customArrows = []
    }),

    setAvailableHints: action((state, payload) => {
        state.availableHints = payload
    }),

    setFtueOptions: action((state, payload) => {
        state.ftueOptions = payload
    }),
}

export default PuzzlesViewModel
